/* The bulk quote quiz (sections/st-quote-quiz.liquid). Without this file the form is every question
   in order and still sends; with it, one screen at a time:
   - Back and Next, a progress count, focus moved to each screen's heading;
   - Enter on a choice means Next, not send (the browser would otherwise submit the form);
   - a question marked "needs an answer" must have one, and a ticked product needs a quantity; the
     message is an alert, tied to its question, and scrolled into view;
   - "Your brief" lists the answers as they are given;
   - the logo note is a status region, filled only when a logo option is chosen;
   - the answers (not the contact details) are kept in sessionStorage and cleared once sent;
   - a brief from the quiz funnel's volume route is put in the message box;
   - the details are checked on the send button's click, before any submit event (Shopify's bot
     protection handles submit and moved focus into its own frame); a field's error is tied to it
     only while it shows, and clears as soon as the field is fixed. */
(() => {
  const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  function init(section) {
    const sent = section.querySelector('[data-st-sent]');
    const form = section.querySelector('form.st-quiz__card');
    if (!form) return;
    const store = `st-quiz:${form.id}`;
    if (sent) {
      try {
        sessionStorage.removeItem(store);
      } catch (e) {}
      setTimeout(() => sent.querySelector('[data-st-sent-h]')?.focus(), 0);
      return;
    }
    if (form.dataset.stReady) return;
    form.dataset.stReady = 'true';

    // Questions (and a screen heading) carry their step number; two questions can share a screen.
    const groups = new Map();
    form.querySelectorAll('[data-st-step]').forEach((el) => {
      const n = el.dataset.stStep;
      if (!groups.has(n)) groups.set(n, []);
      groups.get(n).push(el);
    });
    const steps = [...groups.values()];
    const top = form.querySelector('[data-st-top]');
    const bar = form.querySelector('[data-st-bar]');
    const count = form.querySelector('[data-st-count]');
    const back = form.querySelector('[data-st-back]');
    const next = form.querySelector('[data-st-next]');
    const submit = form.querySelector('[data-st-submit]');
    const brief = section.querySelector('[data-st-brief]');
    const questions = [...form.querySelectorAll('[data-st-question]')];
    // The note's own server-rendered nodes, taken out and put back, so the region changes (and is
    // announced) only when a logo option is chosen.
    const logoNotes = [...form.querySelectorAll('[data-st-logo-note]')].map((note) => {
      const kept = [...note.childNodes];
      note.replaceChildren();
      return { note, kept };
    });
    let at = 0;

    // The first heading showing: the last screen's price panel (assets/st-quiz-price.js) has its own,
    // hidden unless the order is priced.
    const headingOf = (i) => {
      const first = steps[i][0];
      if (first.matches('.st-quiz__h')) return first;
      return [...first.querySelectorAll('.st-quiz__h')].find((h) => !h.closest('[hidden]') || h.closest('[hidden]') === first) || null;
    };

    try {
      const saved = JSON.parse(sessionStorage.getItem(store) || '{}');
      questions.forEach((q) =>
        q.querySelectorAll('input').forEach((input) => {
          if (saved[`${input.name}=${input.value}`]) input.checked = true;
        })
      );
      if (Number.isInteger(saved.__at)) at = Math.min(saved.__at, steps.length - 1);
    } catch (e) {}

    // A volume order from the quiz funnel (assets/st-funnel.js) arrives with its answers as the message.
    try {
      const brief = sessionStorage.getItem('st-funnel-brief');
      const body = form.querySelector('textarea[name="contact[body]"]');
      if (brief && body && !body.value) {
        body.value = brief;
        sessionStorage.removeItem('st-funnel-brief');
      }
    } catch (e) {}

    const save = () => {
      const data = { __at: at };
      questions.forEach((q) =>
        q.querySelectorAll('input:checked').forEach((input) => {
          data[`${input.name}=${input.value}`] = true;
        })
      );
      try {
        sessionStorage.setItem(store, JSON.stringify(data));
      } catch (e) {}
    };

    const qtyFor = (box) => form.querySelector(`[data-st-qty-for="${CSS.escape(box.dataset.stProduct)}"]`);

    const sync = () => {
      form.querySelectorAll('[data-st-product]').forEach((box) => {
        const qty = qtyFor(box);
        if (!qty) return;
        qty.hidden = !box.checked;
        // An unticked product's quantity is never sent (a hidden radio still submits), and a ticked
        // one starts on the first band, as v5 does: the question is a rough number, and an error on
        // a question the visitor never saw cost them a step (parity review 2026-10-09).
        const radios = [...qty.querySelectorAll('input[type="radio"]')];
        radios.forEach((r) => (r.disabled = !box.checked));
        if (box.checked && radios.length && !radios.some((r) => r.checked)) radios[0].checked = true;
      });
      logoNotes.forEach(({ note, kept }) => {
        const on = !!note.closest('[data-st-question]')?.querySelector('input[data-st-logo]:checked');
        if (on && !note.childNodes.length) note.replaceChildren(...kept);
        if (!on && note.childNodes.length) note.replaceChildren();
      });
      renderBrief();
    };

    const answerOf = (q) => {
      const radio = q.querySelector('.st-tiles input[type="radio"]:checked');
      if (radio) return radio.value;
      return [...q.querySelectorAll('.st-tiles input[type="checkbox"]:checked')]
        .map((box) => {
          const label = box.dataset.stValue || box.value;
          const qty = box.dataset.stProduct ? qtyFor(box)?.querySelector('input:checked') : null;
          return qty ? `${label} (${qty.value.toLowerCase()})` : label;
        })
        .join(', ');
    };

    function renderBrief() {
      if (!brief) return;
      const list = brief.querySelector('[data-st-brief-list]');
      const empty = brief.querySelector('[data-st-brief-empty]');
      list.replaceChildren();
      questions.forEach((q) => {
        const value = answerOf(q);
        if (!value) return;
        const row = document.createElement('div');
        const dt = document.createElement('dt');
        const dd = document.createElement('dd');
        dt.textContent = q.dataset.stKey;
        dd.textContent = value;
        row.append(dt, dd);
        list.append(row);
      });
      empty.hidden = list.children.length > 0;
    }

    const flag = (el, message, on) => {
      message.hidden = !on;
      if (on && message.id) el.setAttribute('aria-describedby', message.id);
      else el.removeAttribute('aria-describedby');
    };

    const valid = (group) => {
      let firstBad = null;
      group
        .filter((el) => el.matches('[data-st-question]'))
        .forEach((q) => {
          const need = q.querySelector('[data-st-need]');
          const answered = !q.matches('[data-st-required]') || !!q.querySelector('.st-tiles input:checked');
          if (need) flag(q, need, !answered);
          if (!answered && !firstBad) firstBad = { focus: q.querySelector('.st-tiles input'), show: need };
          q.querySelectorAll('[data-st-product]:checked').forEach((box) => {
            const qty = qtyFor(box);
            const qneed = qty?.querySelector('[data-st-qty-need]');
            const has = !!qty?.querySelector('input:checked');
            if (qneed) qneed.hidden = has;
            if (!has && !firstBad) firstBad = { focus: qty?.querySelector('input'), show: qneed };
          });
        });
      if (firstBad) {
        firstBad.show?.scrollIntoView({ block: 'center' });
        firstBad.focus?.focus({ preventScroll: true });
      }
      return !firstBad;
    };

    const show = (i, focus) => {
      at = i;
      steps.forEach((group, n) => group.forEach((el) => (el.hidden = n !== i)));
      const last = i === steps.length - 1;
      back.hidden = i === 0;
      next.hidden = last;
      submit.hidden = !last;
      const tpl = (last ? top.dataset.countLast : top.dataset.count) || '';
      count.textContent = tpl.replace('__N__', String(i + 1)).replace('__T__', String(steps.length));
      // Progress as a scale, not a width, so it can glide without moving layout (st-motion.css).
      if (bar) bar.style.transform = `scaleX(${(i + 1) / steps.length})`;
      // The screen just moved to eases in (st-motion.css; none under reduced motion). Only on a
      // move (focus follows), not on first load or when sent back to a question.
      if (focus) steps[i].forEach((el) => {
        el.classList.remove('st-step-in');
        void el.offsetWidth;
        el.classList.add('st-step-in');
      });
      save();
      if (focus) setTimeout(() => headingOf(i)?.focus(), 0);
    };

    next.addEventListener('click', () => {
      if (valid(steps[at])) show(at + 1, true);
    });
    back.addEventListener('click', () => show(at - 1, true));

    // A tap or click on a single-choice answer moves on, as v5 does (parity review 2026-10-09: on a
    // phone the Next button sat below the screen on most steps). Pointer only: a keyboard user moving
    // through the choices with the arrow keys changes the answer on every key, so they keep Next
    // (WCAG 3.2.2, no change of context on input).
    let pointerPick = false;
    form.addEventListener('pointerdown', (event) => {
      pointerPick = !!event.target.closest('.st-tiles');
    });
    const advanceIfSingle = (event) => {
      const input = event.target;
      if (!pointerPick || input.type !== 'radio' || !input.closest('.st-tiles')) return;
      pointerPick = false;
      const group = steps[at];
      const qs = group.filter((el) => el.matches('[data-st-question]'));
      const single = qs.length === 1 && !qs[0].querySelector('.st-tiles input[type="checkbox"]');
      if (!single || at >= steps.length - 1 || !group.some((el) => el.contains(input))) return;
      const from = at;
      setTimeout(() => {
        if (at === from && input.checked && valid(steps[at])) show(at + 1, true);
      }, 320);
    };

    form.addEventListener('change', (event) => {
      advanceIfSingle(event);
      const q = event.target.closest('[data-st-question]');
      const need = q?.querySelector('[data-st-need]');
      if (need && event.target.checked) flag(q, need, false);
      const qneed = event.target.closest('[data-st-qty-for]')?.querySelector('[data-st-qty-need]');
      if (qneed) qneed.hidden = true;
      sync();
      save();
    });

    const fields = [...form.querySelectorAll('[data-st-field]')];
    const fieldBad = (input) => {
      const value = input.value.trim();
      return (input.required && !value) || (input.type === 'email' && !!value && !EMAIL.test(value));
    };
    const markField = (input, bad) => {
      const error = document.getElementById(input.dataset.stErr);
      if (bad) input.setAttribute('aria-invalid', 'true');
      else input.removeAttribute('aria-invalid');
      if (error) flag(input, error, bad);
    };
    // A flagged field clears as soon as it is fixed, and leaves the summary.
    fields.forEach((input) =>
      ['input', 'blur'].forEach((type) =>
        input.addEventListener(type, () => {
          if (!input.hasAttribute('aria-invalid') || fieldBad(input)) return;
          markField(input, false);
          form.querySelector(`[data-st-summary-list] a[href="#${CSS.escape(input.id)}"]`)?.closest('li')?.remove();
          const summary = form.querySelector('[data-st-summary]');
          if (summary && !summary.querySelector('li')) summary.hidden = true;
        })
      )
    );

    const check = (event) => {
      // Enter on a choice submits the form in the browser; before the last screen it means Next.
      if (at < steps.length - 1) {
        event.preventDefault();
        next.click();
        return;
      }
      for (let i = 0; i < steps.length - 1; i++) {
        if (!valid(steps[i])) {
          event.preventDefault();
          show(i, false);
          valid(steps[i]);
          return;
        }
      }
      const problems = fields.filter((input) => {
        const bad = fieldBad(input);
        markField(input, bad);
        return bad;
      });
      const summary = form.querySelector('[data-st-summary]');
      if (!problems.length) {
        if (summary) summary.hidden = true;
        return;
      }
      event.preventDefault();
      if (!summary) return;
      summary.querySelector('[data-st-summary-list]').replaceChildren(
        ...problems.map((input) => {
          const li = document.createElement('li');
          const a = document.createElement('a');
          a.href = `#${input.id}`;
          a.textContent = input.dataset.stLabel || input.id;
          a.addEventListener('click', (e) => {
            e.preventDefault();
            input.focus();
          });
          li.append(a);
          return li;
        })
      );
      summary.hidden = false;
      summary.focus();
    };
    submit.addEventListener('click', check);
    form.addEventListener('submit', check);

    // "Start the quiz" links to the section's anchor: move focus there, not just the scroll.
    const anchor = section.id;
    const toQuiz = () => setTimeout(() => headingOf(at)?.focus({ preventScroll: true }), 0);
    document.addEventListener('click', (event) => {
      const link = event.target.closest('a[href]');
      if (link && anchor && link.getAttribute('href') === `#${anchor}`) toQuiz();
    });
    if (anchor && location.hash === `#${anchor}`) toQuiz();

    top.hidden = false;
    if (brief) brief.hidden = false;
    sync();
    show(at, false);
  }

  const run = () => document.querySelectorAll('.st-quiz').forEach((el) => init(el.closest('section') || el));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  document.addEventListener('shopify:section:load', run);
})();
